import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { EventEmitterService } from '../../services/event-emitter.service';
import { BranchesService } from '../../services/branches.service';
import * as L from 'leaflet';
import { ThirdpartyService } from '../../services/thirdparty.service';
import { debounceTime, Subject, switchMap, tap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { CountryISO } from 'ngx-intl-tel-input';
import { OrdersService } from '../../services/orders.service';
import { GeoSearchControl, OpenStreetMapProvider } from 'leaflet-geosearch';
import { TeamMembersService } from '../../services/team-members.service';
import { AuthenticationService } from '../../services/authentication.service';
import { AccountTypes } from '../../models/account_types';
import { BusinessesService } from '../../services/businesses.service';

@Component({
  standalone: false,
  selector: 'app-branch-popup',
  templateUrl: './branch-popup.component.html',
  styleUrl: './branch-popup.component.scss',
})
export class BranchPopupComponent implements OnInit, AfterViewInit, OnDestroy {
  user;
  title;
  type = '';
  formGroup: FormGroup;
  branch;
  mymap: L.Map;
  marker;
  weekdays = [];
  businesses = [];
  selectedBusiness = null;
  branches = [];
  selectedBranch = null;
  thirdParties = [];
  selectedThirdParty = null;
  teamMembers = [];
  selectedTeamMembers = null;
  branchTeamMembers = [];
  disableButton = false;
  selectedCountryISO: CountryISO;
  accountTypes = AccountTypes;

  searchBusinesses = new Subject<string>();
  searchBusinessesNoResults = 'no-results-found';
  searchBranches = new Subject<string>();
  searchBranchesNoResults = 'no-results-found';
  searchThirdParties = new Subject<string>();
  searchThirdPartiesNoResults = 'no-results-found';
  searchTeamMembers = new Subject<string>();
  searchTeamMembersNoResults = 'no-results-found';

  constructor(
    public bsModalRef: BsModalRef,
    private branchesService: BranchesService,
    private businessesService: BusinessesService,
    private thirdPartiesService: ThirdpartyService,
    private teamMembersService: TeamMembersService,
    private eventEmitterService: EventEmitterService,
    private translateService: TranslateService,
    private ordersService: OrdersService,
    private authenticationService: AuthenticationService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  async ngOnInit() {
    console.log('this.branch:');
    console.log(this.branch);
    await this.getUser();
    this.setupWeekdays();
    this.getBranchTeamMembers();
    this.getAllThirdParties('');
    this.getAllBusinesses('');
    this.getAllBranches('');
    this.getAllRiders('');
    this.setupForm();

    this.changeDetectorRef.detectChanges();
    this.getUserLocation(true);
  }

  ngAfterViewInit(): void {
    this.setupThirdPartiesSearch();
    this.setupTeamMembersSearch();
    this.setupBusinessesSearch();
    this.setupBranchesSearch();
  }

  ngOnDestroy() {
    if (this.mymap) {
      this.mymap.off();
      this.mymap.remove();
    }
  }

  async getUser() {
    this.user = await this.authenticationService.getUserFromToken();
  }

  setupWeekdays() {
    this.weekdays = [
      { id: 'monday', name: this.translateService.instant('monday') },
      { id: 'tuesday', name: this.translateService.instant('tuesday') },
      { id: 'wednesday', name: this.translateService.instant('wednesday') },
      { id: 'thursday', name: this.translateService.instant('thursday') },
      { id: 'friday', name: this.translateService.instant('friday') },
      { id: 'saturday', name: this.translateService.instant('saturday') },
      { id: 'sunday', name: this.translateService.instant('sunday') },
    ];
  }

  setupForm() {
    let offDays = [];
    if (this.branch && this.branch.off_days) {
      if (typeof this.branch.off_days === 'string') {
        const parsedDays = JSON.parse(this.branch.off_days);
        offDays = this.weekdays.filter((w) => parsedDays.includes(w.id));
      } else {
        offDays = this.weekdays.filter((w) =>
          this.branch.off_days.includes(w.id),
        );
      }
    }

    let own_branch = 'external-branch';
    if (this.user.user.business.type == AccountTypes.Merchant) {
      own_branch = 'my-branch';
    }
    if (this.branch && this.branch.business_id == this.user.user.business.id) {
      own_branch = 'my-branch';
    }

    this.formGroup = new FormGroup({
      name: new FormControl(
        this.branch ? this.branch.name : '',
        Validators.required,
      ),
      phone: new FormControl(
        this.branch ? this.branch.phone : '',
        Validators.required,
      ),
      latitude: new FormControl(
        this.branch ? this.branch.latitude : '',
        Validators.required,
      ),
      longitude: new FormControl(
        this.branch ? this.branch.longitude : '',
        Validators.required,
      ),
      start_hour: new FormControl(
        this.branch ? this.branch.start_hour : '09:00',
        Validators.required,
      ),
      end_hour: new FormControl(
        this.branch ? this.branch.end_hour : '17:00',
        Validators.required,
      ),
      off_days: new FormControl(offDays),
      delivery_type: new FormControl(
        this.branch ? this.branch.delivery_type : 'team',
        Validators.required,
      ),
      own_branch: new FormControl(own_branch, Validators.required),
      delivery_radius: new FormControl(
        this.branch ? this.branch.delivery_radius : 20,
        [Validators.required, Validators.min(0)],
      ),
      eta_min: new FormControl(this.branch ? this.branch.eta_min : 20, [
        Validators.min(0),
      ]),
      eta_max: new FormControl(this.branch ? this.branch.eta_max : 30, [
        Validators.min(0),
      ]),
      team_members: new FormControl(
        this.branch ? this.branch.team_members : null,
      ),
      third_party_id: new FormControl(
        this.branch ? this.branch.third_party_id : null,
      ),
      business_id: new FormControl(
        this.branch ? this.branch.business_id : null,
      ),
      parent_branch_id: new FormControl(
        this.branch ? this.branch.parent_branch_id : null,
      ),
      shipping_cost: new FormControl(
        this.branch ? this.branch.shipping_cost : 0,
        [Validators.required, Validators.min(0)],
      ),
      min_order: new FormControl(this.branch ? this.branch.min_order : 0, [
        Validators.min(0),
      ]),
    });

    this.formGroup
      .get('own_branch')
      .valueChanges.subscribe((value) =>
        this.updateExternalBranchValidators(value),
      );
    this.updateExternalBranchValidators(own_branch);
  }

  updateExternalBranchValidators(ownBranch: string) {
    const businessControl = this.formGroup.get('business_id');
    const parentBranchControl = this.formGroup.get('parent_branch_id');
    if (ownBranch === 'external-branch') {
      businessControl.setValidators(Validators.required);
      parentBranchControl.setValidators(Validators.required);
    } else {
      businessControl.clearValidators();
      parentBranchControl.clearValidators();
    }
    businessControl.updateValueAndValidity();
    parentBranchControl.updateValueAndValidity();
  }

  getBranchTeamMembers() {
    if (!this.branch) {
      return;
    }
    this.teamMembersService.getBranchTeamMembers(this.branch.id).subscribe(
      (response: any) => {
        console.log('response:');
        console.log(response);
        let list = [];
        response.items.forEach((item) => {
          list.push(item.teamMember);
        });
        this.branchTeamMembers = list;
        this.selectedTeamMembers = list;
        this.formGroup.get('team_members').setValue(list);
      },
      (error) => {
        console.log('error:');
        console.log(error);
      },
    );
  }

  getAllThirdParties(search) {
    const apiData: any = {};
    if (search && search.trim().length > 0) {
      apiData.search = search;
    }
    this.thirdPartiesService.getAllThirdParties(apiData).subscribe(
      (response: any) => {
        console.log('response:');
        console.log(response);
        this.searchThirdPartiesNoResults = 'no-results-found';
        this.thirdParties = response.items;

        if (this.branch && this.branch.third_party_id) {
          this.selectedThirdParty = this.thirdParties.find(
            (tp) => tp.id == this.branch.third_party_id,
          );
        }
      },
      (error) => {
        console.log('error:');
        console.log(error);
        this.searchThirdPartiesNoResults = 'no-results-found';
      },
    );
  }

  setupThirdPartiesSearch() {
    this.searchThirdParties
      .pipe(
        tap(() => (this.searchThirdPartiesNoResults = 'loading')),
        debounceTime(3000),
        switchMap(async (searchText) => this.getAllThirdParties(searchText)),
      )
      .subscribe();
  }

  getAllBusinesses(search) {
    const apiData: any = {};
    if (search && search.trim().length > 0) {
      apiData.search = search;
    }
    this.businessesService.getAllBusinesses(apiData).subscribe(
      (response: any) => {
        console.log('response:');
        console.log(response);
        this.searchBusinessesNoResults = 'no-results-found';
        this.businesses = response.items;

        if (this.branch && this.branch.business_id) {
          this.selectedBusiness = this.businesses.find(
            (tp) => tp.id == this.branch.business_id,
          );
        }
      },
      (error) => {
        console.log('error:');
        console.log(error);
        this.searchBusinessesNoResults = 'no-results-found';
      },
    );
  }

  setupBusinessesSearch() {
    this.searchBusinesses
      .pipe(
        tap(() => (this.searchBusinessesNoResults = 'loading')),
        debounceTime(3000),
        switchMap(async (searchText) => this.getAllBusinesses(searchText)),
      )
      .subscribe();
  }

  getAllBranches(search) {
    const apiData: any = {};
    if (search && search.trim().length > 0) {
      apiData.search = search;
    }
    this.branchesService.getAllParentBranches(apiData).subscribe(
      (response: any) => {
        console.log('response:');
        console.log(response);
        this.searchBranchesNoResults = 'no-results-found';
        this.branches = response.items;

        if (this.branches.length == 1) {
          this.selectedBranch = this.branches[0];
          this.formGroup.get('parent_branch_id').setValue(this.branches[0].id);
        }
        if (this.branch && this.branch.parent_branch_id) {
          this.selectedBranch = this.branches.find(
            (tp) => tp.id == this.branch.parent_branch_id,
          );
        }
      },
      (error) => {
        console.log('error:');
        console.log(error);
        this.searchBranchesNoResults = 'no-results-found';
      },
    );
  }

  setupBranchesSearch() {
    this.searchBranches
      .pipe(
        tap(() => (this.searchBranchesNoResults = 'loading')),
        debounceTime(3000),
        switchMap(async (searchText) => this.getAllBranches(searchText)),
      )
      .subscribe();
  }

  getAllRiders(search) {
    const apiData: any = {};
    if (search && search.trim().length > 0) {
      apiData.search = search;
    }
    this.teamMembersService.getAllRiders(apiData).subscribe(
      (response: any) => {
        console.log('response:');
        console.log(response);
        this.searchTeamMembersNoResults = 'no-results-found';
        this.teamMembers = response.items;

        this.teamMembers.forEach((item) => {
          item.name = item.rider.name;
        });
      },
      (error) => {
        console.log('error:');
        console.log(error);
        this.searchTeamMembersNoResults = 'no-results-found';
      },
    );
  }

  setupTeamMembersSearch() {
    this.searchTeamMembers
      .pipe(
        tap(() => (this.searchTeamMembersNoResults = 'loading')),
        debounceTime(3000),
        switchMap(async (searchText) => this.getAllRiders(searchText)),
      )
      .subscribe();
  }

  closeModal() {
    this.bsModalRef.hide();
  }

  setOffDays(event) {
    this.formGroup
      .get('off_days')
      .setValue(Array.isArray(event.value) ? event.value : []);
  }

  clearOffDays() {
    this.formGroup.get('off_days').setValue([]);
  }

  setThirdParty(event) {
    this.selectedThirdParty = event.value;
    this.formGroup
      .get('third_party_id')
      .setValue(event.value ? event.value.id : null);
  }

  setBusiness(event) {
    this.selectedBusiness = event.value;
    this.formGroup
      .get('business_id')
      .setValue(event.value ? event.value.id : null);
  }

  setBranch(event) {
    this.selectedBranch = event.value;
    this.formGroup
      .get('parent_branch_id')
      .setValue(event.value ? event.value.id : null);
  }

  setTeamMembers(event) {
    this.selectedTeamMembers = event.value;
    this.formGroup.get('team_members').setValue(event.value);
  }

  buttonClicked() {
    this.formGroup.markAllAsTouched();

    if (this.formGroup.invalid) {
      return;
    }

    const formValue = { ...this.formGroup.value };
    formValue.off_days = Array.isArray(formValue.off_days)
      ? formValue.off_days.map((day) => day.id)
      : [];

    if (formValue.own_branch == 'my-branch') {
      formValue.business_id = this.user.user.business.id;
      formValue.parent_branch_id = 0;
    }

    if (this.disableButton == true) {
      return;
    }
    this.disableButton = true;

    if (this.type === 'new-branch') {
      this.branchesService.createBranch(formValue).subscribe(
        (response) => {
          console.log('response:');
          console.log(response);
          this.eventEmitterService.callAllBranches();
          this.closeModal();
          this.disableButton = false;
        },
        (error) => {
          console.log('error:');
          console.log(error);
          this.disableButton = false;
        },
      );
    }
    if (this.type === 'edit-branch') {
      this.branchesService.updateBranch(this.branch.id, formValue).subscribe(
        (response) => {
          console.log('response:');
          console.log(response);
          this.eventEmitterService.callAllBranches();
          this.closeModal();
          this.disableButton = false;
        },
        (error) => {
          console.log('error:');
          console.log(error);
          this.disableButton = false;
        },
      );
    }
  }

  async setupMap() {
    if (this.mymap) {
      this.mymap.remove();
    }

    this.mymap = L.map('map', {
      center: [this.formGroup.value.latitude, this.formGroup.value.longitude],
      zoom: 10,
      renderer: L.canvas(),
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
      className: 'map-tiles',
    }).addTo(this.mymap);

    const searchForAddress =
      this.translateService.instant('search-for-address');

    const provider = new OpenStreetMapProvider();
    const searchControl = GeoSearchControl({
      provider: provider,
      style: 'bar',
      showMarker: false,
      showPopup: false,
      autoClose: true,
      retainZoomLevel: false,
      animateZoom: true,
      keepResult: true,
      searchLabel: searchForAddress,
    });
    this.mymap.addControl(searchControl);

    const markerIcon = L.icon({
      iconUrl: '../../../assets/map-marker.svg',
      iconSize: [30, 30],
    });

    this.marker = L.marker(
      [this.formGroup.value.latitude, this.formGroup.value.longitude],
      {
        icon: markerIcon,
        draggable: true,
      },
    ).addTo(this.mymap);

    this.mymap.on('click', (e) => {
      var latlng = e.latlng;

      this.formGroup.get('latitude').setValue(latlng.lat);
      this.formGroup.get('longitude').setValue(latlng.lng);

      this.marker.setLatLng([
        this.formGroup.value.latitude,
        this.formGroup.value.longitude,
      ]);
      this.updateSelectedCountryISO(latlng.lat, latlng.lng);
    });

    this.marker.on('dragend', (e) => {
      var latlng = e.target.getLatLng();

      this.formGroup.get('latitude').setValue(latlng.lat);
      this.formGroup.get('longitude').setValue(latlng.lng);
      this.updateSelectedCountryISO(latlng.lat, latlng.lng);
    });

    this.mymap.on('geosearch/showlocation', (e: any) => {
      const { location } = e;
      this.marker.setLatLng([location.y, location.x]);
      this.formGroup.get('latitude').setValue(location.y);
      this.formGroup.get('longitude').setValue(location.x);
      this.updateSelectedCountryISO(location.y, location.x);
    });

    setTimeout(() => {
      this.mymap.invalidateSize();
    }, 30000);
  }

  getUserLocation(useData) {
    // If editing an existing branch with coordinates, use those values for the map
    if (useData) {
      if (this.branch && this.branch.latitude && this.branch.longitude) {
        this.setupMap();
        this.updateSelectedCountryISO(
          this.branch.latitude,
          this.branch.longitude,
        );
        return;
      }
    }

    // Otherwise get user location for new branches
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position: GeolocationPosition) => {
          this.formGroup.get('latitude').setValue(position.coords.latitude);
          this.formGroup.get('longitude').setValue(position.coords.longitude);
          this.setupMap();
          this.updateSelectedCountryISO(
            position.coords.latitude,
            position.coords.longitude,
          );
        },
        (error: GeolocationPositionError) => {
          console.log('error:');
          console.log(error);
          this.formGroup.get('latitude').setValue(42.6635);
          this.formGroup.get('longitude').setValue(21.1638);
          this.setupMap();
          this.updateSelectedCountryISO(52.52, 13.405);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        },
      );
    } else {
      console.log('Geolocation is not supported by this browser.');
      this.formGroup.get('latitude').setValue(42.6635);
      this.formGroup.get('longitude').setValue(21.1638);
      this.setupMap();
      this.updateSelectedCountryISO(52.52, 13.405);
    }
  }

  async updateSelectedCountryISO(latitude: number, longitude: number) {
    try {
      const response = await this.ordersService.getNominatimAddress(
        latitude,
        longitude,
      );

      if (response && response.address && response.address.country_code) {
        const countryCode = response.address.country_code.toLowerCase();

        // Convert country code to CountryISO enum value
        // CountryISO enum uses lowercase country codes as values
        let userCountry: CountryISO;

        // Find the matching CountryISO enum value
        Object.keys(CountryISO).forEach((key) => {
          if (CountryISO[key].toLowerCase() === countryCode) {
            userCountry = CountryISO[key];
          }
        });

        if (userCountry) {
          // If the user's country is already in the array but not first, remove it
          this.selectedCountryISO = userCountry;
        }
      }
    } catch (error) {
      console.error('Error getting country from coordinates:', error);
    }
  }
}
